import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  computed,
  inject,
  input,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  AssignShiftPopupManagerService,
  ReloadSignal,
} from '@wilson/activities/services';
import { FeaturePurePipe } from '@wilson/authorization';
import {
  Activity,
  FeatureName,
  OPEN_FIND_STAFF,
  OperationStatus,
  ResolvedShiftWithActivity,
  RoleAction,
  RolePermissionSubject,
  Score,
  Shift,
} from '@wilson/interfaces';
import { EventCategory } from '@wilson/matomo';
import { OpenFindStaffButtonHelperService } from '@wilson/non-domain-specific/shift-helpers/assignments';
import { AssignShiftHelperService } from '@wilson/non-domain-specific/shift-helpers/core';
import { ShiftScoresService } from '@wilson/shift/core';
import { ShiftAssignmentService } from '@wilson/shifts';
import { cloneDeep } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { MatomoTracker } from 'ngx-matomo-client';
import { Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'wilson-shift-assignment',
  templateUrl: './shift-assignment.component.html',
  styleUrls: ['./shift-assignment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AssignShiftPopupManagerService, AssignShiftHelperService],
  standalone: false,
})
export class ShiftAssignmentComponent implements OnChanges, OnDestroy {
  private readonly modalService = inject(NzModalService);
  private readonly translateService = inject(TranslateService);
  private readonly shiftAssignmentService = inject(ShiftAssignmentService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly tracker = inject(MatomoTracker);
  private readonly featurePipe = inject(FeaturePurePipe);
  private readonly store = inject(Store);
  private readonly shiftScoresService = inject(ShiftScoresService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly messageService = inject(NzMessageService);
  private readonly helperService = inject(OpenFindStaffButtonHelperService);
  private readonly assignShiftPopupManagerService = inject(
    AssignShiftPopupManagerService,
  );

  @Input() shift!: ResolvedShiftWithActivity | Shift;
  disabled = input(false);
  @Output() selectedAssignment = new EventEmitter();
  @Output() reloadShift = new EventEmitter();

  #assignShiftHelperService = inject(AssignShiftHelperService);

  faInfoCircle = faInfoCircle;

  public smartScore!: string;
  public prefScore!: string;
  public smartScoreProgress!: string;
  public prefScoreProgress!: string;
  public subscription!: Subscription;
  public OperationStatus = OperationStatus;
  public canAssignShift = computed(
    () =>
      !this.disabled() &&
      this.#assignShiftHelperService.isAssignAllowedSignal(),
  );

  hasPreferencePermission = false;

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['shift']) {
      this.#assignShiftHelperService.updateShiftAssignmentPermission(
        changes['shift'].currentValue ? [changes['shift'].currentValue] : [],
      );
    }

    this.hasPreferencePermission = await firstValueFrom(
      this.featurePipe.transform(FeatureName.Preference),
    );

    if (this.shift?.id && this.shift.userId) {
      this.subscription = this.shiftAssignmentService
        .getShiftScoreByUserId(this.shift.id as string, this.shift.userId)
        .pipe(take(1))
        .subscribe((score) => {
          this.calculateScores(score);
          this.cdRef.detectChanges();
        });
    } else {
      this.resetScores();
    }
    if (
      this.shift &&
      this.activatedRoute.snapshot.fragment === OPEN_FIND_STAFF
    ) {
      if (this.canAssignShift()) {
        this.openFindStaffPopup();
      } else {
        this.messageService.error(
          this.translateService.instant(
            'shift.assignment.cannot_unassign_already_started_or_submitted',
          ),
        );
      }
    }
  }

  public calculateScores(score: Score[]): void {
    this.smartScore = this.shiftScoresService.calculateSmartScore(score);
    this.smartScoreProgress = `width: ${(Number(this.smartScore) * 100) / 5}%`;

    if (this.hasPreferencePermission) {
      this.prefScore = this.shiftScoresService.calculatePrefScore(score);
      this.prefScoreProgress = `width: ${(Number(this.prefScore) * 100) / 5}%`;
    }
  }

  public resetScores(): void {
    this.smartScore = '0.0';
    this.prefScore = '0.0';
    this.smartScoreProgress = 'width: 0%';
    this.prefScoreProgress = 'width: 0%';
  }

  public async openFindStaffPopup(): Promise<void> {
    this.tracker.trackEvent(
      EventCategory.Shift,
      'open_assign_staff_visual_popup',
    );

    const modalResult: ReloadSignal | undefined = await firstValueFrom(
      (
        await this.assignShiftPopupManagerService.openFindStaffPopup(
          cloneDeep(this.shift) as Shift & { activities: Activity[] },
        )
      ).afterClose,
    );
    if (modalResult?.shouldReload) {
      this.reloadShift.emit();
    }
  }

  public async unassignStaff(): Promise<NzModalRef> {
    return this.modalService.confirm({
      nzTitle: this.translateService.instant('page.shift_disposition.title'),
      nzContent: this.translateService.instant(
        'page.shift_disposition.unassign_message',
      ),
      nzOkText: this.translateService.instant('general.yes'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCancelText: this.translateService.instant('general.no'),
      nzOnOk: () => {
        this.selectedAssignment.emit(null);
      },
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
